import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

const CourseFilters = ({
  levels,
  durations,
  lessonsNo,
  selectedLevels,
  selectedDurations,
  selectedLessonsNo,
  handleLevelChange,
  handleDurationChange,
  handleLessonsNoChange,
  resetFilters,
  getLevelColor,
  showLevelFilter = true,
  // New props for categories
  selectedCategories = [],
  handleCategoryChange,
}) => {
  const [categories, setCategories] = useState([]);
  
  // Fetch categories from the JSON file with cache control
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Add cache-busting parameter to prevent browser caching
        const cacheBuster = `?v=${Date.now()}`;
        const categoryUrl = `https://qallix-public.s3.ap-southeast-1.amazonaws.com/QallixAcademy/SoftwareCourses/category.json${cacheBuster}`;
        
        const response = await fetch(categoryUrl, {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch categories: ${response.status}`);
        }
        
        const data = await response.json();
        // Filter only active categories and sort by order
        const activeCategories = data.categories
          .filter(category => category.isActive)
          .sort((a, b) => a.order - b.order)
          .map(category => category.name);
          
        setCategories(activeCategories);
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };
    
    fetchCategories();
  }, []);

  return (
    <Box sx={{
      backgroundColor: '#fff',
      borderRadius: 3,
      boxShadow: 0,
      p: 3,
      mb: 3
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex' }}>
          <FilterAltIcon sx={{alignSelf:'center', mr:'5px'}}></FilterAltIcon>
          <Typography variant="subtitle1" sx={{
            fontWeight: 'bold',
            fontFamily: "'Satoshi', sans-serif",
            fontSize: '1.2rem'
          }}>
            Filters
          </Typography>
        </Box>
        <Button
          variant="text"
          onClick={resetFilters}
          sx={{
            color: '#7C3AED',
            textTransform: 'none',
            fontFamily: "'Satoshi', sans-serif",
            fontWeight: 'bold'
          }}
        >
          Reset All
        </Button>
      </Box>

      {/* Category Filter - Added as the first filter */}
      {categories.length > 0 && (
        <Accordion defaultExpanded={window.innerWidth >= 900} sx={{
          boxShadow: 'none',
          '&:before': { display: 'none' },
          fontFamily: "'Satoshi', sans-serif",
        }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold' }}>Category</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {categories.map((category) => (
              <FormControlLabel
                key={category}
                control={
                  <Checkbox
                    checked={selectedCategories.includes(category)}
                    onChange={() => handleCategoryChange(category)}
                    size="small"
                    sx={{
                      color: '#7C3AED',
                      '&.Mui-checked': {
                        color: '#7C3AED',
                      },
                    }}
                  />
                }
                label={category}
                sx={{
                  display: 'block',
                  ml: 0,
                  '& .MuiTypography-root': {
                    fontFamily: "'Satoshi', sans-serif",
                  }
                }}
              />
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Level Filter */}
      {showLevelFilter && (
        <Accordion defaultExpanded={window.innerWidth >= 900} sx={{
          boxShadow: 'none',
          '&:before': { display: 'none' },
          fontFamily: "'Satoshi', sans-serif",
        }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} >
            <Typography sx={{ fontWeight: 'bold' }}>Level</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {levels?.map((level) => (
              <FormControlLabel
                key={level}
                control={
                  <Checkbox
                    checked={selectedLevels?.includes(level)}
                    onChange={() => handleLevelChange(level)}
                    size="small"
                    sx={{
                      color: getLevelColor?.(level),
                      '&.Mui-checked': {
                        color: getLevelColor?.(level),
                      },
                    }}
                  />
                }
                label={level}
                sx={{
                  display: 'block',
                  ml: 0,
                  '& .MuiTypography-root': {
                    fontFamily: "'Satoshi', sans-serif",
                  }
                }}
              />
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Lessons Filter */}
      <Accordion sx={{
        boxShadow: 'none',
        '&:before': { display: 'none' },
        fontFamily: "'Satoshi', sans-serif"
      }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 'bold' }}>Lessons</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {lessonsNo.map((lessonRange) => (
            <FormControlLabel
              key={lessonRange}
              control={
                <Checkbox
                  checked={selectedLessonsNo.includes(lessonRange)}
                  onChange={() => handleLessonsNoChange(lessonRange)}
                  size="small"
                  sx={{
                    color: '#7C3AED',
                    '&.Mui-checked': {
                      color: '#7C3AED',
                    },
                  }}
                />
              }
              label={lessonRange}
              sx={{
                display: 'block',
                ml: 0,
                '& .MuiTypography-root': {
                  fontFamily: "'Satoshi', sans-serif",
                }
              }}
            />
          ))}
        </AccordionDetails>
      </Accordion>

      {/* Duration Filter */}
      <Accordion sx={{
        boxShadow: 'none',
        '&:before': { display: 'none' },
        fontFamily: "'Satoshi', sans-serif"
      }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 'bold' }}>Duration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {durations.map((duration) => (
            <FormControlLabel
              key={duration}
              control={
                <Checkbox
                  checked={selectedDurations.includes(duration)}
                  onChange={() => handleDurationChange(duration)}
                  size="small"
                  sx={{
                    color: '#7C3AED',
                    '&.Mui-checked': {
                      color: '#7C3AED',
                    },
                  }}
                />
              }
              label={duration}
              sx={{
                display: 'block',
                ml: 0,
                '& .MuiTypography-root': {
                  fontFamily: "'Satoshi', sans-serif",
                }
              }}
            />
          ))}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default CourseFilters;